import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Reveal } from './motion';

export function SectionTag({ children, dark = false, center = false }) {
  return (
    <Reveal className={center ? 'text-center' : ''}>
      <span className={`overline !text-sm sm:!text-base ${dark ? 'text-gold' : 'text-electric'}`}>{children}</span>
    </Reveal>
  );
}

export function ArrowBtn({ to, onClick, children, variant = 'primary', testId, className = '', style }) {
  const base = `group inline-flex touch-target items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold tracking-tight transition-[background-color,color,transform,border-color] duration-300 hover:-translate-y-0.5 ${className}`;
  const styles = {
    primary: 'bg-electric text-white hover:bg-navy',
    navy: 'bg-navy text-white hover:bg-electric',
    gold: 'bg-gold text-navy hover:bg-white',
    ghostLight: 'border border-white/30 text-white hover:border-gold hover:text-gold',
    ghostDark: 'border border-navy/25 text-navy hover:border-electric hover:text-electric',
  };
  const inner = (
    <>
      <span>{children}</span>
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (to) {
    return (
      <Link data-testid={testId} to={to} style={style} className={`${base} ${styles[variant]}`}>
        {inner}
      </Link>
    );
  }
  return (
    <button data-testid={testId} onClick={onClick} style={style} className={`${base} ${styles[variant]}`}>
      {inner}
    </button>
  );
}

export function TextLink({ to, children, dark = false, testId }) {
  return (
    <Link
      data-testid={testId}
      to={to}
      className={`group inline-flex touch-target items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold tracking-tight transition-[background-color,color,border-color,transform] duration-300 hover:-translate-y-0.5 ${
        dark
          ? 'border-gold/40 bg-gold/10 text-gold hover:bg-gold hover:text-navy'
          : 'border-electric/30 bg-electric/5 text-electric hover:bg-electric hover:text-white'
      }`}
    >
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </Link>
  );
}

export function Countdown({ target, dark = true, testId = 'countdown' }) {
  const calc = () => {
    const diff = Math.max(0, new Date(target).getTime() - Date.now());
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff / 3600000) % 24),
      m: Math.floor((diff / 60000) % 60),
      s: Math.floor((diff / 1000) % 60),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  const cells = [
    { v: t.d, l: 'Days' },
    { v: t.h, l: 'Hours' },
    { v: t.m, l: 'Minutes' },
    { v: t.s, l: 'Seconds' },
  ];
  return (
    <div data-testid={testId} className="flex items-stretch gap-2 sm:gap-3">
      {cells.map((c) => (
        <div
          key={c.l}
          data-testid={`${testId}-${c.l.toLowerCase()}`}
          className={`flex min-w-[68px] flex-col items-center rounded-2xl border px-3 py-3 sm:min-w-[88px] sm:px-5 sm:py-4 ${
            dark ? 'border-gold/20 bg-navy-800/80' : 'border-navy/10 bg-white'
          }`}
        >
          <span className={`font-mono text-2xl font-semibold tabular-nums sm:text-4xl ${dark ? 'text-gold' : 'text-electric'}`}>
            {String(c.v).padStart(2, '0')}
          </span>
          <span className={`overline mt-1 !text-[10px] ${dark ? 'text-white/50' : 'text-navy/50'}`}>{c.l}</span>
        </div>
      ))}
    </div>
  );
}

export function ChainFlow({ steps, dark = false, testId = 'chain-flow' }) {
  return (
    <div data-testid={testId} className="flex flex-wrap items-center gap-2">
      {steps.map((s, i) => (
        <span key={i} className="flex items-center gap-2">
          <span
            className={`rounded-full border px-4 py-2 text-xs font-semibold tracking-tight sm:text-sm ${
              dark ? 'border-gold/25 bg-navy-800 text-gold' : 'border-navy/15 bg-white text-navy'
            }`}
          >
            {s}
          </span>
          {i < steps.length - 1 && <ArrowRight className={`h-4 w-4 ${dark ? 'text-gold/50' : 'text-electric/60'}`} />}
        </span>
      ))}
    </div>
  );
}

export function FAQ({ items, dark = false, testId = 'faq' }) {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid={testId} className={`divide-y rounded-3xl border ${dark ? 'divide-white/10 border-white/10' : 'divide-navy/10 border-navy/10 bg-white'}`}>
      {items.map((item, i) => (
        <div key={i}>
          <button
            data-testid={`${testId}-q-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full touch-target items-center justify-between gap-4 px-6 py-5 text-left"
          >
            <span className={`text-base font-semibold tracking-tight ${dark ? 'text-white' : 'text-navy'}`}>{item.q}</span>
            <span className={`font-mono text-lg ${dark ? 'text-gold' : 'text-electric'}`}>{open === i ? '−' : '+'}</span>
          </button>
          {open === i && (
            <p data-testid={`${testId}-a-${i}`} className={`px-6 pb-6 text-sm leading-relaxed sm:text-base ${dark ? 'text-white/70' : 'text-navy/70'}`}>
              {item.a}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

import { useContent } from '../lib/content';

export const mediaUrl = (u) => (u?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
export const isVideo = (u) => /\.(mp4|webm|mov)(\?|#|$)/i.test(u || '');

export function PageHero({ tag, lines, intro, children, dark = false, image, ck }) {
  const { ct } = useContent();
  const tTag = ck ? ct(`${ck}.tag`, tag) : tag;
  const tTitle = ck ? ct(`${ck}.title`, null) : null;
  const tIntro = ck ? ct(`${ck}.intro`, intro) : intro;
  const tImage = ck ? ct(`${ck}.image`, null) : null;

  if (tImage) {
    return (
      <header className={`relative overflow-hidden ${dark ? 'bg-navy text-white' : 'bg-paper text-navy'}`}>
        {dark && <div className="bg-grid-dark absolute inset-0" />}
        {dark && <div className="absolute -top-40 right-[-10%] h-[480px] w-[480px] rounded-full bg-electric/25 blur-[140px]" />}
        <div className="wrap relative pb-16 pt-36 sm:pb-24 sm:pt-44">
          <Reveal>
            <div data-testid={`hero-image-${ck}`} className="overflow-hidden rounded-3xl border border-navy/10 shadow-2xl shadow-navy/20">
              {isVideo(tImage) ? (
                <video src={mediaUrl(tImage)} autoPlay muted loop playsInline className="h-auto w-full" />
              ) : (
                <img src={mediaUrl(tImage)} alt={tTitle || tag} className="h-auto w-full" />
              )}
            </div>
          </Reveal>
          {children && <Reveal delay={0.15} className="mt-8 flex flex-wrap items-center gap-4">{children}</Reveal>}
        </div>
      </header>
    );
  }

  return (
    <header className={`relative overflow-hidden ${dark ? 'bg-navy text-white' : 'bg-paper text-navy'}`}>
      {dark && <div className="bg-grid-dark absolute inset-0" />}
      {dark && <div className="absolute -top-40 right-[-10%] h-[480px] w-[480px] rounded-full bg-electric/25 blur-[140px]" />}
      <div className="wrap relative pb-16 pt-36 sm:pb-24 sm:pt-44">
        <SectionTag dark={dark}>{tTag}</SectionTag>
        <div className="mt-6">
          <Reveal delay={0.05}>
            <h1 className="text-display max-w-4xl font-semibold">{tTitle ?? lines}</h1>
          </Reveal>
        </div>
        {tIntro && (
          <Reveal delay={0.15}>
            <p className={`mt-6 max-w-2xl text-base leading-relaxed sm:text-lg ${dark ? 'text-white/70' : 'text-navy/70'}`}>{tIntro}</p>
          </Reveal>
        )}
        {children && <Reveal delay={0.25} className="mt-8 flex flex-wrap items-center gap-4">{children}</Reveal>}
        {image && (
          <Reveal delay={0.3} className="mt-12">
            <div className="overflow-hidden rounded-3xl border border-navy/10 shadow-2xl shadow-navy/20">
              <img src={image} alt="" className="aspect-[21/9] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        )}
      </div>
    </header>
  );
}
